import { MatchStatus } from '@uefadigital/panenka';

import LivePollingInstance from '@live/pollingHandler';
import type { Draw, DrawRound } from '@shared-types/draw.types';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import { formatUrl } from '@utilities/fetch';
import { throttle } from '@utilities/throttle';
import { apiKey, drawApiUrl } from '@utilities/WindowContext';

export type DrawHeaderOptions = {
  drawId: string;
  drawName: string;
  season: string;
  pollInterval?: number;
};

const defaults: Partial<DrawHeaderOptions> = {
  pollInterval: 5000,
};

export default class DrawHeader {
  dom: HTMLElement;
  options: DrawHeaderOptions;
  endPoint: string;
  lastDrawCommentaryStatus: MatchStatus | null;
  lastDrawStatus: MatchStatus | null;
  draw: Draw;
  tabs: HTMLPkTabElement[];
  resultsTab: HTMLPkTabElement;
  potsTab: HTMLPkTabElement;
  firstPoll = true;
  videoWrapper: HTMLElement;

  /**
   * @constructor
   */
  constructor(dom: HTMLElement, options: DrawHeaderOptions) {
    this.dom = dom;
    this.options = {
      ...defaults,
      ...options,
    };

    this.endPoint = formatUrl(drawApiUrl, 'v1/draws', {
      drawId: this.options.drawId,
      optionalFields: ['TEAMS'],
    });
  }

  init() {
    this.tabs = Array.from(this.dom.querySelectorAll('.js-draw-header__tabs .js-draw-header__tab'));
    this.resultsTab = this.dom.querySelector('.js-results');
    this.potsTab = this.dom.querySelector('.js-pots');
    this.videoWrapper = document.querySelector<HTMLElement>('.draw-header__video-wrapper');

    // Listener and Observer
    document.addEventListener('scroll', this.checkScroll);
    this.checkScroll();

    this.tabs.forEach((t) =>
      t.addEventListener('pkTabButtonClick', () => {
        this.selectTab(t);
        // scroll selected content to top
        window.scrollTo({
          top: document.querySelector<HTMLElement>('.tab-content')?.offsetTop - 40,
          behavior: 'smooth',
        });
      }),
    );

    this.register(this.endPoint, this.watchDraw);
  }

  get tabPanels() {
    return [...document.querySelectorAll<HTMLElement>('.tab-pane')];
  }

  selectTab = (tab: HTMLPkTabElement) => {
    if (!tab) return;

    this.tabs.forEach((t) => {
      const selected = t.tabId === tab.tabId;

      t.selected = selected;
      if (!selected) return;

      this.trackTab(t);

      const targetSelector = t.dataset.target;
      this.tabPanels.forEach((panel) => {
        const active = panel.matches(targetSelector);
        panel.classList.toggle('active', active);
        panel.classList.toggle('show', active);
      });
    });
  };

  selectActiveTab = () => {
    // if draw is UPCOMING and pots tab is visible select it
    if (
      this.draw?.status?.eventStatus === MatchStatus.UPCOMING &&
      this.potsTab &&
      !this.potsTab.classList.contains('hide')
    ) {
      this.selectTab(this.potsTab);
      return;
    }

    // select the first visble tab
    const visibleTabs = this.tabs?.filter((t) => !t.classList.contains('hide'));
    this.selectTab(visibleTabs?.[0]);
  };

  /**
   * Watches the draw for status change
   * @param drawObj - The draw endpoint response.
   */
  watchDraw = (drawObj: Draw[]) => {
    // We need to check if the draw is live to start the results watch
    if (!drawObj?.length) return;
    const [draw] = drawObj;
    this.draw = draw;

    if (this.lastDrawCommentaryStatus !== draw.status.commentaryStatus) {
      this.manageCommentary(draw.status.commentaryStatus);
      this.lastDrawCommentaryStatus = draw.status.commentaryStatus;
    }

    this.manageResults(draw.status.resultsAvailable && draw.status.eventStatus !== MatchStatus.UPCOMING, draw.rounds);
    this.managePots(draw.status.potsAvailable, draw.rounds);

    if (this.lastDrawStatus !== draw.status.eventStatus) {
      if (this.lastDrawStatus) {
        GoogleTagManagerHelper.addPageMeta({
          draw_id: draw.id,
          draw_status: draw.status.eventStatus.toUpperCase(),
          draw_name: draw.translations?.title?.EN,
        });
      }

      this.manageDrawPage(draw.status.eventStatus);
      this.lastDrawStatus = draw.status.eventStatus;
    }

    const hiddenTabSelected = this.tabs?.some((t) => t.selected && t.classList.contains('hide'));
    if (this.firstPoll || hiddenTabSelected) {
      this.selectActiveTab();
      this.firstPoll = false;
    }

    if (draw.status.eventStatus === MatchStatus.FINISHED) {
      this.detach();
    }
  };

  trackTab = (el: HTMLElement) => {
    const data = JSON.parse(el.dataset.page) as {
      pageName: string;
      pageSubType: string;
      drawId: string;
      season: string;
    };
    GoogleTagManagerHelper.addPageMeta({
      pageName: data.pageName,
      pageSubType: data.pageSubType,
      draw_id: this.options.drawId,
      draw_name: this.options.drawName ?? this.draw?.translations?.title?.EN,
      draw_status: this.draw?.status.eventStatus.toUpperCase(),
    });
    GoogleTagManagerHelper.addVirtualPage({
      virtPath: data.pageName,
    });
  };

  /**
   * Register a callback to an endpoint
   *
   * @param endpoint.
   * @param callback.
   * @param interval.
   */
  register = (endpoint: string, callback: (resp: Draw[]) => unknown) => {
    const headers = [
      {
        key: 'x-api-key',
        value: apiKey,
      },
    ];
    LivePollingInstance.on(endpoint, headers, this.options.pollInterval, callback, this);
  };

  /**
   * Stops to watch the draw endpoint
   */
  detach() {
    LivePollingInstance.off(this.endPoint, this.watchDraw);
  }

  /**
   * @param newStatus
   */
  manageDrawPage = (newStatus: MatchStatus) => {
    if (!newStatus) return;
    Array.from(document.querySelectorAll<HTMLElement>(`.js-draw`)).forEach((e) =>
      e.classList.toggle('hide', !e.classList.contains(`js-draw-status-${newStatus.toLowerCase()}`)),
    );
  };

  manageResults = (resultsAvailable: boolean, rounds: DrawRound[]) => {
    const showTab =
      resultsAvailable &&
      rounds?.some((r) =>
        r.result?.groups?.some((g) => g.teamSlots?.some((ts) => ts.teamIds?.length || ts.opponents?.length)),
      );
    this.resultsTab?.classList.toggle('hide', !showTab);
  };

  managePots = (potsAvailable: boolean, rounds: DrawRound[]) => {
    const showTab = potsAvailable && rounds?.some((r) => r.pots?.every((p) => p.teamIds?.length));
    this.potsTab?.classList.toggle('hide', !showTab);
  };

  /**
   * @param  newCommentaryStatus
   */
  manageCommentary = (newCommentaryStatus: MatchStatus) => {
    if (!newCommentaryStatus) return;

    Array.from(document.querySelectorAll('.js-draw-commentary')).forEach((el) =>
      el.classList.toggle(
        'hide',
        !el.classList.contains(`js-draw-commentary-status-${newCommentaryStatus.toLowerCase()}`),
      ),
    );
  };

  checkScroll = throttle(() => {
    // Only move elements on portrait mode
    if (window.innerHeight < window.innerWidth) {
      this.videoWrapper?.querySelector<HTMLElement>('.fixable')?.style.removeProperty('top');
      this.videoWrapper?.style.removeProperty('height');
      return;
    }

    const stickyHeight = [
      ...(document.querySelectorAll('.navigation--sticky') ?? []),
      ...(document.querySelectorAll('.affix') ?? []),
    ].reduce((acc, elem) => acc + elem.clientHeight, 0);

    const videoPresent = this.videoWrapper?.clientHeight > 0 && !!this.videoWrapper?.querySelector('.fixable');
    if (videoPresent) {
      const { top: videoTop } = this.videoWrapper.getBoundingClientRect();
      const videoFixed = videoTop < stickyHeight;
      if (videoFixed) {
        this.videoWrapper.style.setProperty('height', `${this.videoWrapper.clientHeight}px`);
      } else {
        this.videoWrapper.style.removeProperty('height');
      }

      this.videoWrapper.classList.toggle('draw-header__video-wrapper--fixed', videoFixed);
      this.videoWrapper.querySelector<HTMLElement>('.fixable')?.style.setProperty('top', `${stickyHeight}px`);
    }

    const videoHeight =
      videoPresent && window.innerWidth < 960
        ? (this.videoWrapper.querySelector<HTMLElement>('.fixable')?.clientHeight ?? 0)
        : 0;

    const { top } = this.dom.getBoundingClientRect();
    const fixed = top < stickyHeight + videoHeight;

    if (fixed) {
      this.dom.style.setProperty('height', `${this.dom.clientHeight}px`);
    } else {
      this.dom.style.removeProperty('height');
    }

    this.dom.classList.toggle('draw-navigation--fixed', fixed);
    this.dom.querySelector<HTMLElement>('.fixable')?.style.setProperty('top', `${stickyHeight + videoHeight}px`);
  }, 100);
}
